import { StateCreator } from 'zustand';
import { Task, TaskComment } from '../../types/task';
import { Department } from '../../types';
import { produce } from 'immer';

export interface TaskSlice {
  tasks: Task[];
  taskHistory: Task[][];
  historyIndex: number;
  searchQuery: string;
  setTasks: (tasks: Task[]) => void;
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task> | Task) => void;
  updateTaskStatus: (update: { taskId: string; status: Task['status'] }) => void;
  updateTaskAcknowledgment: (taskId: string) => void;
  acknowledgeSubtask: (taskId: string, subtaskId: string) => void;
  completeSubtask: (taskId: string, subtaskId: string) => void;
  addTaskComment: (taskId: string, comment: TaskComment) => void;
  getDepartmentTasks: (department: Department) => Task[];
  getAssignedTasks: (userId: string) => Task[];
  deleteTask: (taskId: string) => void;
  setSearchQuery: (query: string) => void;
  getFilteredTasks: () => Task[];
  undoTaskAction: () => void;
  redoTaskAction: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

export const createTaskSlice: StateCreator<TaskSlice> = (set, get) => ({
  tasks: [],
  taskHistory: [[]],
  historyIndex: 0,
  searchQuery: '',
  
  setTasks: (tasks) => {
    set(produce((state) => {
      state.tasks = tasks;
      state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), tasks];
      state.historyIndex = state.taskHistory.length - 1;
    }));
  },

  addTask: (task) => {
    set(produce((state) => {
      const processedTask = {
        ...task,
        createdAt: new Date(task.createdAt),
        updatedAt: new Date(task.updatedAt),
        dueDate: new Date(task.dueDate),
        comments: task.comments || [],
        subtasks: task.subtasks?.map(st => ({
          ...st,
          dueDate: st.dueDate ? new Date(st.dueDate) : undefined,
          acknowledgedAt: st.acknowledgedAt ? new Date(st.acknowledgedAt) : undefined,
          completedAt: st.completedAt ? new Date(st.completedAt) : undefined
        }))
      };

      // Add task to beginning of list for immediate visibility
      state.tasks.unshift(processedTask);
      state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
      state.historyIndex = state.taskHistory.length - 1;
    }));
  },

  updateTask: (taskId, updates) => {
    set(produce((state) => {
      const taskIndex = state.tasks.findIndex(t => t.id === taskId);
      if (taskIndex !== -1) {
        state.tasks[taskIndex] = {
          ...state.tasks[taskIndex],
          ...(updates instanceof Object ? updates : {}),
          updatedAt: new Date()
        };
        state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
        state.historyIndex = state.taskHistory.length - 1;
      }
    }));
  },

  updateTaskStatus: ({ taskId, status }) => {
    set(produce((state) => {
      const task = state.tasks.find(t => t.id === taskId);
      if (task) {
        task.status = status;
        task.updatedAt = new Date();
        state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
        state.historyIndex = state.taskHistory.length - 1;
      }
    }));
  },

  updateTaskAcknowledgment: (taskId) => {
    set(produce((state) => {
      const task = state.tasks.find(t => t.id === taskId);
      if (task) {
        task.acknowledged = true;
        task.acknowledgedAt = new Date();
        task.updatedAt = new Date();
        state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
        state.historyIndex = state.taskHistory.length - 1;
      }
    }));
  },

  acknowledgeSubtask: (taskId, subtaskId) => {
    set(produce((state) => {
      const task = state.tasks.find(t => t.id === taskId);
      if (task && task.subtasks) {
        const subtask = task.subtasks.find(st => st.id === subtaskId);
        if (subtask) {
          subtask.acknowledged = true;
          subtask.acknowledgedAt = new Date();
          task.updatedAt = new Date();
          state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
          state.historyIndex = state.taskHistory.length - 1;
        }
      }
    }));
  },

  completeSubtask: (taskId, subtaskId) => {
    set(produce((state) => {
      const task = state.tasks.find(t => t.id === taskId);
      if (task && task.subtasks) {
        const subtask = task.subtasks.find(st => st.id === subtaskId);
        if (subtask) {
          subtask.status = 'COMPLETED';
          subtask.completedAt = new Date();
          task.updatedAt = new Date();
          
          // Check if all subtasks are completed
          const allSubtasksCompleted = task.subtasks.every(st => st.status === 'COMPLETED');
          if (allSubtasksCompleted) {
            task.status = 'COMPLETED';
          }
          state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
          state.historyIndex = state.taskHistory.length - 1;
        }
      }
    }));
  },

  addTaskComment: (taskId, comment) => {
    set(produce((state) => {
      const task = state.tasks.find(t => t.id === taskId);
      if (task) {
        if (!task.comments) task.comments = [];
        task.comments.unshift(comment); // Add to beginning of array for immediate visibility
        task.updatedAt = new Date();
        state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
        state.historyIndex = state.taskHistory.length - 1;
      }
    }));
  },

  getDepartmentTasks: (department) => {
    const state = get();
    return state.tasks.filter(task => task.department === department);
  },

  getAssignedTasks: (userId) => {
    const state = get();
    return state.tasks.filter(task => task.assignedTo === userId);
  },

  deleteTask: (taskId) => {
    set(produce((state) => {
      state.tasks = state.tasks.filter(t => t.id !== taskId);
      state.taskHistory = [...state.taskHistory.slice(0, state.historyIndex + 1), [...state.tasks]];
      state.historyIndex = state.taskHistory.length - 1;
    }));
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  getFilteredTasks: () => {
    const state = get();
    const query = state.searchQuery.toLowerCase();
    
    if (!query) return state.tasks;

    return state.tasks.filter(task => 
      task.title.toLowerCase().includes(query) ||
      task.description.toLowerCase().includes(query) ||
      task.department.toLowerCase().includes(query) ||
      task.status.toLowerCase().includes(query)
    );
  },

  undoTaskAction: () => {
    set(produce((state) => {
      if (state.historyIndex > 0) {
        state.historyIndex--;
        state.tasks = [...state.taskHistory[state.historyIndex]];
      }
    }));
  },

  redoTaskAction: () => {
    set(produce((state) => {
      if (state.historyIndex < state.taskHistory.length - 1) {
        state.historyIndex++;
        state.tasks = [...state.taskHistory[state.historyIndex]];
      }
    }));
  },

  canUndo: () => {
    const state = get();
    return state.historyIndex > 0;
  },

  canRedo: () => {
    const state = get();
    return state.historyIndex < state.taskHistory.length - 1;
  }
});